// API service for Swipey.ai authentication endpoints
import { AuthRedirectIntent } from '../types'
import { getEfTransactionId } from './everflow'
import { getTrackingParams } from './tracking'
import { setStorage, getStorage, setAuth } from './storage'

const SWIPEY_BASE_URL = process.env.REACT_APP_SWIPEY_BASE_URL
const API_BASE_URL = `${SWIPEY_BASE_URL}/api/v1`
const API_KEY = process.env.REACT_APP_API_KEY

// Lander identifier sent to the backend so registrations can be attributed to
// this LP, regardless of whether the user signs up via email or Google.
export const LP_SOURCE = 'create_ai_slut'

// Resolve the post-signup landing on the Swipey side based on which CTA the
// user clicked:
//   * chat → /paywall?selected_slug={slug}. xDate's quiz-landing-premium-page
//     persists `selected_slug` to localStorage and `premiumOnClose` in
//     usePaymentSuccessHandlers redirects to /chats/{slug} after payment.
//   * create-ai → /create-ai?become_premium_after_signup=true. /create-ai
//     handles its own paywall mid-flow; the flag matches kawaii-slut/ai-pornub
//     so the rest of the Swipey app (analytics, modal queue) treats this as a
//     post-signup paywall arrival.
export type LandingTarget = {
  kind: 'paywall' | 'create-ai'
  basePath: string
  params: Record<string, string>
}

export const resolveLandingTarget = (
  intent: AuthRedirectIntent,
): LandingTarget => {
  if (intent.kind === 'chat' && intent.characterSlug) {
    return {
      kind: 'paywall',
      basePath: '/paywall',
      params: { selected_slug: intent.characterSlug },
    }
  }
  return {
    kind: 'create-ai',
    basePath: '/create-ai',
    params: { become_premium_after_signup: 'true' },
  }
}

// Anonymous "Create My Slut" redirect — matches the other landers that send
// the user to /create-ai (kawaii-slut, ai-pornub, swipeymatch): we forward
// straight to the Swipey /create-ai funnel WITHOUT an auth_token, so the user
// registers on the Swipey side. No pre-lander token exchange happens, which
// avoids the "User not found" the on-lander auth flow hit (a freshly registered
// token lives under Redis `external_registration:` while `auth_type=
// login_pre_lander` made the platform look it up under `external_login:`).
export const buildCreateAiRedirectUrl = (): string => {
  const url = new URL(`${SWIPEY_BASE_URL}/create-ai`)
  url.searchParams.set('become_premium_after_signup', 'true')

  const trackingParams = getTrackingParams()
  Object.entries(trackingParams).forEach(([key, value]) => {
    if (value) url.searchParams.set(key, value as string)
  })

  const ef_transaction_id = getEfTransactionId()
  if (ef_transaction_id) {
    url.searchParams.set('ef_transaction_id', ef_transaction_id)
  }

  return url.toString()
}

// OAuth `path` field — the backend builds `${frontend}${path}` to redirect
// post-OAuth, so we return the same destination + query string the email/
// password flow would use.
export const resolveOauthPath = (intent: AuthRedirectIntent): string => {
  const target = resolveLandingTarget(intent)
  const qs = new URLSearchParams(target.params).toString()
  return qs ? `${target.basePath}?${qs}` : target.basePath
}

// Types and Interfaces

export interface UTMData {
  path?: string
  [key: string]: string | undefined
}

export interface APIResponse {
  success: boolean
  message?: string
  redirectUrl?: string
  authToken?: string
  token?: string
  error?: string
  isSubscribed?: boolean
  otp_sent?: boolean
  otpExpiry?: string
  loginUrl?: string
}

// Common headers for all API requests
const getHeaders = (): HeadersInit => ({
  'Content-Type': 'application/json',
  'x-api-key': API_KEY || '',
})

// External-click-id keys cache. The list of keys (e.g. ['twclid','gclid','fbclid','ttclid'])
// is owned by the backend; we fetch it once per page load and reuse it for every auth call.
let externalClickIdKeys: string[] | null = null
let externalClickIdKeysPromise: Promise<string[]> | null = null

// Snapshot the landing-URL query params into sessionStorage on module load.
// We read externalClickIds from this snapshot — NOT from localStorage `utm_data`
// (which merges across visits and leaks stale click-ids), and NOT from
// `window.location.search` at submit time (SPA navigation strips it).
// sessionStorage clears when the tab closes → each tab starts fresh.
const LANDING_URL_PARAMS_KEY = 'landing_url_params'
;(() => {
  if (typeof window === 'undefined') return
  try {
    if (sessionStorage.getItem(LANDING_URL_PARAMS_KEY)) return
    const snapshot: Record<string, string> = {}
    new URLSearchParams(window.location.search).forEach((value, key) => {
      snapshot[key] = value
    })
    sessionStorage.setItem(LANDING_URL_PARAMS_KEY, JSON.stringify(snapshot))
  } catch {
    /* ignore */
  }
})()

// Fetch the list of click-id keys the backend wants us to forward. Cached for the page lifetime;
// in-flight requests are deduped via `externalClickIdKeysPromise`. Failures / timeouts fall back
// to [] so auth + OAuth redirects can't be blocked by this endpoint — externalClickIds gets sent as {}.
const EXTERNAL_CLICK_ID_KEYS_TIMEOUT_MS = 3000

const fetchExternalClickIdKeys = async (): Promise<string[]> => {
  if (Array.isArray(externalClickIdKeys)) return externalClickIdKeys
  if (externalClickIdKeysPromise) return externalClickIdKeysPromise

  externalClickIdKeysPromise = (async () => {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
    const timeoutId = controller
      ? setTimeout(() => controller.abort(), EXTERNAL_CLICK_ID_KEYS_TIMEOUT_MS)
      : null
    try {
      const response = await fetch(`${API_BASE_URL}/marketing/external-click-id-keys`, {
        method: 'GET',
        headers: getHeaders(),
        signal: controller?.signal,
      })
      if (!response.ok) {
        externalClickIdKeys = []
        return externalClickIdKeys
      }
      const data = await response.json()
      externalClickIdKeys = Array.isArray(data) ? data : []
      return externalClickIdKeys
    } catch (err) {
      console.warn('[externalClickIds] keys fetch failed, falling back to []', err)
      externalClickIdKeys = []
      return externalClickIdKeys
    } finally {
      if (timeoutId) clearTimeout(timeoutId)
      externalClickIdKeysPromise = null
    }
  })()

  return externalClickIdKeysPromise
}

// Build the externalClickIds object the backend expects: for each key returned by
// /marketing/external-click-id-keys, pull the value from this tab's landing-URL snapshot.
// Source rationale (see LANDING_URL_PARAMS_KEY comment above):
//   - NOT localStorage.utm_data → leaks click-ids from previous visits
//   - NOT window.location.search at submit time → SPA navigation strips it
export const getExternalClickIds = async (): Promise<Record<string, string | null>> => {
  const keys = await fetchExternalClickIdKeys()
  let landingParams: Record<string, string> = {}
  try {
    const raw = sessionStorage.getItem(LANDING_URL_PARAMS_KEY)
    if (raw) landingParams = (JSON.parse(raw) as Record<string, string>) || {}
  } catch {
    landingParams = {}
  }
  return keys.reduce<Record<string, string | null>>((acc, key) => {
    const value = landingParams[key]
    acc[key] = value == null || value === '' ? null : value
    return acc
  }, {})
}

// Generic API request function
const apiRequest = async <T = any>(
  endpoint: string,
  method: string = 'GET',
  body: any = null
): Promise<T> => {
  try {
    const config: RequestInit = {
      method,
      headers: getHeaders(),
    }

    if (body) {
      config.body = JSON.stringify(body)
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, config)
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`)
    }

    return data
  } catch (error) {
    throw error
  }
}

// UTM data management
export const collectAndStoreUTMData = (): UTMData => {
  // Initialize tracking if not already done
  if (typeof window !== 'undefined' && (window as any).initTracking) {
    (window as any).initTracking()
  }

  const trackingParams = getTrackingParams()

  // Read URL params, but skip empty values. A URL like `?ef_transaction_id=&utm_source=`
  // is usually a template with un-substituted macros; those empty strings must not
  // shadow real cookie/URL values downstream.
  const currentUrlParams = new URLSearchParams(window.location.search)
  const currentParams: { [key: string]: string } = {}
  currentUrlParams.forEach((value, key) => {
    if (value !== '') currentParams[key] = value
  })

  const merged: { [key: string]: any } = {
    ...trackingParams,
    ...currentParams,
  }

  // Strip any stale/empty ef_transaction_id from prior visits; cookie wins downstream.
  if (!merged.ef_transaction_id) {
    delete merged.ef_transaction_id
  }

  const utmObj: UTMData = {
    ...merged,
    path: '/paywall',
  }

  // Store UTM data using our storage utility
  setStorage('utm_data', utmObj)
  return utmObj
}

// Pre-lander account creation API
export const createPreLanderAccount = async (
  email: string,
  password: string,
  ab_testing?: any
): Promise<APIResponse> => {
  const utmRaw = (getStorage('utm_data') as Record<string, any>) || {}
  const ef_transaction_id = getEfTransactionId()
  const externalClickIds = await getExternalClickIds()

  // Strip any stale/empty ef_transaction_id from utm before spreading so the cookie/URL
  // value resolved by getEfTransactionId() is the only one that ends up in the payload.
  const { ef_transaction_id: _staleEf, ...utm } = utmRaw

  const payload: Record<string, any> = {
    ...utm,
    email,
    password,
    ab_testing: ab_testing || {},
    lp_source: LP_SOURCE,
    externalClickIds,
  }
  if (ef_transaction_id) {
    payload.ef_transaction_id = ef_transaction_id
  }

  const data = await apiRequest<APIResponse>('/auth/pre-lander', 'POST', payload)

  if (data.message && data.message.includes('User with this email already exists')) {
    return {
      success: false,
      error: 'Email already used',
      message: 'Email already used',
    }
  }

  return data
}

// Complete login flow handler
export const handleLoginFlow = async (
  email: string,
  password: string,
  ab_testing: any,
  redirectIntent: AuthRedirectIntent
): Promise<APIResponse> => {
  try {
    const utmRaw = (getStorage('utm_data') as Record<string, any>) || {}
    const ef_transaction_id = getEfTransactionId()
    const externalClickIds = await getExternalClickIds()

    // Strip any stale/empty ef_transaction_id from utm before spreading.
    const { ef_transaction_id: _staleEf, ...utm } = utmRaw

    const payload: Record<string, any> = {
      ...utm,
      email,
      password,
      ab_testing: ab_testing || {},
      lp_source: LP_SOURCE,
      externalClickIds,
    }
    if (ef_transaction_id) {
      payload.ef_transaction_id = ef_transaction_id
    }

    const data = await apiRequest<APIResponse>('/auth/pre-lander/login', 'POST', payload)

    const authToken = data.authToken || data.token
    const user = data

    if (authToken) {
      setAuth(authToken)

      const isSubscribed = user?.isSubscribed === true
      const target = resolveLandingTarget(redirectIntent)

      // "Create My Slut" CTA always lands on /create-ai (its own flow handles
      // the paywall mid-funnel). Girl-card CTA routes through /paywall with
      // `selected_slug` so xDate's premiumOnClose can hop to /chats/{slug}
      // after payment — unless the user is already subscribed, in which case
      // we send them straight to the chat.
      let url: URL
      if (target.kind === 'create-ai') {
        url = new URL(`${SWIPEY_BASE_URL}${target.basePath}`)
        Object.entries(target.params).forEach(([key, value]) =>
          url.searchParams.set(key, value),
        )
      } else if (isSubscribed && target.params.selected_slug) {
        url = new URL(
          `${SWIPEY_BASE_URL}/chats/${encodeURIComponent(target.params.selected_slug)}`,
        )
      } else {
        url = new URL(`${SWIPEY_BASE_URL}${target.basePath}`)
        Object.entries(target.params).forEach(([key, value]) =>
          url.searchParams.set(key, value),
        )
        url.searchParams.set('from_landing', 'true')
      }

      url.searchParams.set('auth_token', authToken)
      url.searchParams.set('lp_source', LP_SOURCE)
      url.searchParams.set(
        'auth_type',
        isSubscribed || target.kind === 'create-ai'
          ? 'login_pre_lander'
          : 'login_paywall',
      )

      Object.entries(utm).forEach(([key, value]) => {
        if (value) url.searchParams.set(key, value as string)
      })

      // Cookie/URL ef_transaction_id beats any stale value carried in utm.
      if (ef_transaction_id) {
        url.searchParams.set('ef_transaction_id', ef_transaction_id)
      } else {
        url.searchParams.delete('ef_transaction_id')
      }

      return {
        success: true,
        redirectUrl: url.toString(),
      }
    }

    return {
      success: false,
      message: 'Please check your email or password',
    }
  } catch (error: any) {
    return {
      success: false,
      message: 'Please check your email or password',
    }
  }
}

// ---- Magic-link / email-only OTP flow (create-ai-slut-magicLink = ON) ----

// Append the same tracking params we put on the password-flow paywall URL onto
// the loginUrl the backend returns so the paywall sees the right context.
const decorateEmailOnlyRedirect = async (loginUrl?: string): Promise<string | null> => {
  if (!loginUrl) return null

  const url = new URL(loginUrl)
  const utmRaw = (getStorage('utm_data') as Record<string, any>) || {}
  const ef_transaction_id = getEfTransactionId()
  const { ef_transaction_id: _staleEf, ...utm } = utmRaw

  Object.entries(utm).forEach(([key, value]) => {
    if (value && !url.searchParams.has(key)) {
      url.searchParams.set(key, value as string)
    }
  })

  const forwardClickIds = await getExternalClickIds()
  Object.entries(forwardClickIds).forEach(([key, value]) => {
    if (value && !url.searchParams.has(key)) url.searchParams.set(key, String(value))
  })

  if (!url.searchParams.has('type')) url.searchParams.set('type', 'realistic')
  if (!url.searchParams.has('auth_type')) url.searchParams.set('auth_type', 'login_paywall')
  url.searchParams.set('from_landing', 'true')

  if (ef_transaction_id) {
    url.searchParams.set('ef_transaction_id', ef_transaction_id)
  } else {
    url.searchParams.delete('ef_transaction_id')
  }

  return url.toString()
}

// POST /auth/pre-lander-with-email-only — used by both signup and login when
// the magic-link flag is ON. Returns either:
//   - otp_sent: true + otpExpiry (existing email → caller shows OTP modal)
//   - authToken + loginUrl       (new email → caller navigates directly)
export const submitEmailOnly = async (
  email: string,
  { isResend = false, ab_testing }: { isResend?: boolean; ab_testing?: any } = {}
): Promise<APIResponse> => {
  const utmRaw = (getStorage('utm_data') as Record<string, any>) || {}
  const ef_transaction_id = getEfTransactionId()
  const externalClickIds = await getExternalClickIds()
  const { ef_transaction_id: _staleEf, ...utm } = utmRaw

  const payload: Record<string, any> = {
    ...utm,
    email,
    ab_testing: ab_testing || {},
    isResend,
    lp_source: 'create_ai_slut',
    externalClickIds,
  }
  if (ef_transaction_id) {
    payload.ef_transaction_id = ef_transaction_id
  }

  const data = await apiRequest<APIResponse>(
    '/auth/pre-lander-with-email-only',
    'POST',
    payload
  )

  if (data?.success === false) return data

  if (data?.authToken) setAuth(data.authToken)

  const redirectUrl = await decorateEmailOnlyRedirect(data?.loginUrl)
  if (redirectUrl) data.redirectUrl = redirectUrl

  return data
}

// POST /auth/pre-lander/login-with-otp — verifies the OTP, returns the same
// shape as the auto-login branch of submitEmailOnly.
export const verifyPreLanderOtp = async (
  email: string,
  otp: string
): Promise<APIResponse> => {
  const data = await apiRequest<APIResponse>(
    '/auth/pre-lander/login-with-otp',
    'POST',
    { email, otp }
  )

  if (data?.success === false) return data

  const authToken = data?.authToken || data?.token
  if (authToken) setAuth(authToken)

  const redirectUrl = await decorateEmailOnlyRedirect(data?.loginUrl)
  if (redirectUrl) data.redirectUrl = redirectUrl

  return data
}

// Google auth URL
export const getGoogleAuthUrl = (): string => `${SWIPEY_BASE_URL}/api/v1/auth/google/login`

// Swipey base URL
export const getSwipeyBaseUrl = (): string => SWIPEY_BASE_URL || ''

// Request password reset (send code)
export const requestPasswordReset = async (email: string): Promise<APIResponse> => {
  return apiRequest<APIResponse>('/auth/reset-password', 'POST', { email })
}

// Verify OTP for password reset
export const verifyPasswordOtp = async (email: string, otp: string): Promise<APIResponse> => {
  return apiRequest<APIResponse>('/auth/verify-reset-password-otp', 'POST', { email, otp })
}

// Set new password using token
export const updatePassword = async (token: string, newPassword: string): Promise<APIResponse> => {
  return apiRequest<APIResponse>(`/auth/set-new-password?token=${token}`, 'POST', {
    newPassword,
  })
}
